import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Anordnung der Navigation.
 *
 * `sidebar` – ständig sichtbare Leiste am linken Rand. Alle Bereiche liegen
 *   offen, dafür bleiben rund 260 px Breite dauerhaft belegt.
 * `topbar`  – waagerechte Menüzeile mit aufklappbaren Gruppen. Gibt die volle
 *   Breite für Tabellen, Kanban und Karten frei; ein Bereich ist dafür erst
 *   nach einem Klick sichtbar.
 *
 * Die Wahl gehört zum Arbeitsplatz, nicht zum Konto: Sie wird pro Gerät im
 * Browser gespeichert – wie die Theme-Einstellung auch.
 */

export type NavLayout = 'sidebar' | 'topbar';

const STORAGE_KEY = 'ff.navLayout';

interface LayoutContextValue {
  navLayout: NavLayout;
  setNavLayout: (layout: NavLayout) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

function readStored(): NavLayout {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'topbar' || stored === 'sidebar' ? stored : 'sidebar';
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [navLayout, setNavLayoutState] = useState<NavLayout>(readStored);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, navLayout);
    // Auch am Wurzelelement vermerken, damit Stile ohne React-Kontext greifen.
    document.documentElement.dataset.nav = navLayout;
  }, [navLayout]);

  const setNavLayout = useCallback((layout: NavLayout) => setNavLayoutState(layout), []);

  const value = useMemo<LayoutContextValue>(() => ({ navLayout, setNavLayout }), [navLayout, setNavLayout]);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useNavLayout(): LayoutContextValue {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('useNavLayout muss innerhalb von <LayoutProvider> verwendet werden.');
  return context;
}
